import { useCallback, useEffect, useMemo, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import { getList, getListById } from "../../services/services";
import { ENDPOINT } from "../../services/api-endpoint";
import {
  buildCompareRows,
  buildParamMetaByRowKey,
  resolveCompareOps,
  summarizeMachines,
  summarizeTechnologies,
  type RecipeCells,
  type CompareTableRow,
} from "../../lib/recipe-compare";
import type { WoRowLite } from "../../lib/recipe-groups";
import "./trurecipe-pages.scss";
import { formatRecipeCardTitle } from "./trurecipe-display";

type RecipeListRow = {
  id: number;
  name?: string;
  cardTitle?: string;
  status?: string;
  paramCount?: number;
  materialCategoryName?: string | null;
  materialGradeNames?: string[];
};

type RecipeDetail = {
  id: number;
  name: string;
  status: string;
  workflowId: number;
  workflowname?: string | null;
  materialCategoryName?: string | null;
  materialGradeNames?: string[];
  paramCount?: number;
  cardTitle?: string | null;
  payload?: { cells?: RecipeCells } | null;
};

type WfOp = WoRowLite & { machineName?: string | null };

type WorkflowDetail = {
  id: number;
  workflowname?: string | null;
  workflowoperations?: WfOp[];
};

function materialLine(r: RecipeDetail | null): string {
  if (!r) return "—";
  const grades = (r.materialGradeNames ?? []).filter(Boolean).join(", ");
  const cat = (r.materialCategoryName ?? "").trim();
  if (grades) return grades;
  if (cat) return cat;
  return "—";
}

function statusPresentation(status: string | undefined): { label: string; variant: "active" | "draft" | "pending" } {
  const s = (status ?? "draft").trim().toLowerCase();
  if (s === "approved") return { label: "Active", variant: "active" };
  if (s === "pending" || s.includes("review")) return { label: "Pending", variant: "pending" };
  return { label: "Draft", variant: "draft" };
}

function useRecipeSide(selectedId: string, otherId: string) {
  const [detail, setDetail] = useState<RecipeDetail | null>(null);
  const [wf, setWf] = useState<WorkflowDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const id = Number(selectedId);
    if (!Number.isFinite(id) || id <= 0) {
      setDetail(null);
      setWf(null);
      return;
    }
    if (otherId && Number(otherId) === id) {
      setDetail(null);
      setWf(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const raw = await getListById(ENDPOINT.RECIPE_LIST_BY_ID, id);
        if (cancelled) return;
        if (!raw || typeof raw !== "object") {
          setDetail(null);
          setWf(null);
          return;
        }
        const r = raw as RecipeDetail;
        setDetail(r);
        const wfId = Number(r.workflowId);
        if (Number.isFinite(wfId) && wfId > 0) {
          const wfRaw = await getListById(ENDPOINT.WORKFLOWS_LIST_BY_ID, wfId);
          if (cancelled) return;
          const unwrapped = Array.isArray(wfRaw) ? wfRaw[0] : wfRaw;
          setWf(unwrapped && typeof unwrapped === "object" ? (unwrapped as WorkflowDetail) : null);
        } else {
          setWf(null);
        }
      } catch {
        if (!cancelled) {
          setDetail(null);
          setWf(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId, otherId]);

  return { detail, wf, loading };
}

const CompareRecipes = () => {
  const [list, setList] = useState<RecipeListRow[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [idA, setIdA] = useState("");
  const [idB, setIdB] = useState("");

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const rows = await getList(ENDPOINT.RECIPE_LIST, {});
      setList(Array.isArray(rows) ? (rows as RecipeListRow[]) : []);
    } catch {
      setList([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const sideA = useRecipeSide(idA, idB);
  const sideB = useRecipeSide(idB, idA);

  const cellsA = (sideA.detail?.payload?.cells ?? {}) as RecipeCells;
  const cellsB = (sideB.detail?.payload?.cells ?? {}) as RecipeCells;

  const opsA = useMemo(
    () => resolveCompareOps((sideA.wf?.workflowoperations ?? []) as WoRowLite[], cellsA),
    [sideA.wf, cellsA],
  );
  const opsB = useMemo(
    () => resolveCompareOps((sideB.wf?.workflowoperations ?? []) as WoRowLite[], cellsB),
    [sideB.wf, cellsB],
  );

  const metaA = useMemo(() => buildParamMetaByRowKey(opsA), [opsA]);
  const metaB = useMemo(() => buildParamMetaByRowKey(opsB), [opsB]);

  const compareRows: CompareTableRow[] = useMemo(() => {
    if (!idA || !idB || Number(idA) === Number(idB)) return [];
    if (!sideA.detail || !sideB.detail) return [];
    return buildCompareRows(cellsA, cellsB, metaA, metaB);
  }, [idA, idB, sideA.detail, sideB.detail, cellsA, cellsB, metaA, metaB]);

  const bothReady = Boolean(idA && idB && sideA.detail && sideB.detail && Number(idA) !== Number(idB));
  const tableBusy = Boolean(idA && sideA.loading) || Boolean(idB && sideB.loading);

  const optionsA = useMemo(
    () => list.filter((r) => String(r.id) !== idB),
    [list, idB],
  );
  const optionsB = useMemo(
    () => list.filter((r) => String(r.id) !== idA),
    [list, idA],
  );

  const shortTitle = (name: string, max = 28) => {
    const t = name.trim();
    if (t.length <= max) return t;
    return `${t.slice(0, max - 1)}…`;
  };

  const renderSummary = (side: typeof sideA, label: "A" | "B") => {
    const id = label === "A" ? idA : idB;
    if (!id) {
      return (
        <p className="tv-recipe-compare__summary-placeholder">
          {label === "A" ? "Select first recipe" : "Select second recipe"}
        </p>
      );
    }
    if (side.loading) {
      return (
        <div className="tv-recipe-compare__summary-loading">
          <Spinner animation="border" size="sm" />
          <span>Loading…</span>
        </div>
      );
    }
    if (!side.detail) {
      return <p className="tv-recipe-compare__summary-placeholder">Could not load recipe.</p>;
    }
    const r = side.detail;
    const ops = resolveCompareOps((side.wf?.workflowoperations ?? []) as WoRowLite[], (r.payload?.cells ?? {}) as RecipeCells);
    const st = statusPresentation(r.status);
    const title = formatRecipeCardTitle(
      (r.cardTitle && String(r.cardTitle).trim()) || r.name,
      "Recipe",
    );
    const pCount =
      typeof r.paramCount === "number" && Number.isFinite(r.paramCount) && r.paramCount > 0
        ? r.paramCount
        : Math.max(
            0,
            Math.round(
              Object.keys((r.payload?.cells ?? {}) as RecipeCells).filter(
                (k) =>
                  /-[tnx]$/.test(k) &&
                  String((r.payload?.cells as RecipeCells)[k] ?? "").trim() !== "",
              ).length / 3,
            ),
          );

    return (
      <div className="tv-recipe-compare__summary-body">
        <div className="tv-recipe-compare__summary-title">{title}</div>
        <dl className="tv-recipe-compare__summary-dl">
          <div className="tv-recipe-compare__summary-item">
            <dt>Mfg. technology</dt>
            <dd>{summarizeTechnologies(ops)}</dd>
          </div>
          <div className="tv-recipe-compare__summary-item">
            <dt>Material</dt>
            <dd>{materialLine(r)}</dd>
          </div>
          <div className="tv-recipe-compare__summary-item">
            <dt>Machine</dt>
            <dd>{summarizeMachines(ops)}</dd>
          </div>
          <div className="tv-recipe-compare__summary-item tv-recipe-compare__summary-item--inline">
            <dt>Status</dt>
            <dd>
              <span className={`tv-recipe-compare__pill tv-recipe-compare__pill--${st.variant}`}>{st.label}</span>
            </dd>
          </div>
          <div className="tv-recipe-compare__summary-item">
            <dt>Parameters</dt>
            <dd>{pCount}</dd>
          </div>
        </dl>
      </div>
    );
  };

  return (
    <div className="tv-mes-v2-page tv-recipe-compare-page">
      <div className="tv-mes-v2-shell">
        <div className="tv-recipe-compare__pickers">
          <div className="tv-recipe-compare__card">
            <span className="tv-recipe-compare__card-label">Recipe A</span>
            <div className="tv-recipe-compare__select-wrap">
              <Form.Select
                className="tv-recipe-compare__select"
                aria-label="Recipe A"
                value={idA}
                disabled={listLoading}
                title={
                  idA
                    ? (list.find((r) => String(r.id) === idA)?.cardTitle ??
                        list.find((r) => String(r.id) === idA)?.name ??
                        "")
                    : ""
                }
                onChange={(e) => setIdA(e.target.value)}
              >
                <option value="">Select first recipe</option>
                {optionsA.map((r) => (
                  <option key={r.id} value={String(r.id)}>
                    {formatRecipeCardTitle(r.cardTitle ?? r.name, `Recipe #${r.id}`)}
                  </option>
                ))}
              </Form.Select>
            </div>
            {renderSummary(sideA, "A")}
          </div>
          <div className="tv-recipe-compare__card">
            <span className="tv-recipe-compare__card-label">Recipe B</span>
            <div className="tv-recipe-compare__select-wrap">
              <Form.Select
                className="tv-recipe-compare__select"
                aria-label="Recipe B"
                value={idB}
                disabled={listLoading}
                title={
                  idB
                    ? (list.find((r) => String(r.id) === idB)?.cardTitle ??
                        list.find((r) => String(r.id) === idB)?.name ??
                        "")
                    : ""
                }
                onChange={(e) => setIdB(e.target.value)}
              >
                <option value="">Select second recipe</option>
                {optionsB.map((r) => (
                  <option key={r.id} value={String(r.id)}>
                    {formatRecipeCardTitle(r.cardTitle ?? r.name, `Recipe #${r.id}`)}
                  </option>
                ))}
              </Form.Select>
            </div>
            {renderSummary(sideB, "B")}
          </div>
        </div>

        <div className="tv-recipe-compare__panel">
          {!bothReady ? (
            <div className="tv-recipe-compare__empty" role="status">
              <i className="fa fa-code-fork tv-recipe-compare__empty-icon" aria-hidden />
              <p className="tv-recipe-compare__empty-text">
                Select two recipes to compare their parameters side-by-side.
              </p>
            </div>
          ) : tableBusy ? (
            <div className="tv-recipe-compare__table-loading">
              <Spinner animation="border" size="sm" />
              <span>Building comparison…</span>
            </div>
          ) : compareRows.length === 0 ? (
            <div className="tv-recipe-compare__empty tv-recipe-compare__empty--muted">
              <p className="tv-recipe-compare__empty-text mb-0">No parameter cells to compare for this pair.</p>
            </div>
          ) : (
            <div className="tv-recipe-compare__table-wrap">
              <table className="tv-recipe-compare__table">
                <thead>
                  <tr>
                    <th>Parameter</th>
                    <th title={sideA.detail?.name ?? ""}>{shortTitle(sideA.detail?.name ?? "Recipe A")}</th>
                    <th title={sideB.detail?.name ?? ""}>{shortTitle(sideB.detail?.name ?? "Recipe B")}</th>
                    <th className="tv-recipe-compare__th-diff">Difference</th>
                  </tr>
                </thead>
                <tbody>
                  {compareRows.map((row) => (
                    <tr key={row.rowKey}>
                      <td className="tv-recipe-compare__td-param">{row.parameter}</td>
                      <td>{row.valueA}</td>
                      <td>{row.valueB}</td>
                      <td className="tv-recipe-compare__td-diff">
                        {row.different ? (
                          <span className="tv-recipe-compare__badge-diff">Different</span>
                        ) : (
                          <span className="tv-recipe-compare__badge-same">Same</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompareRecipes;
